import { IconButton, Snackbar as MuiSnackbar } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { Close, Error, Info, Warning } from '@material-ui/icons';
import { capitalize } from 'lodash';
import * as React from 'react';

import { ISnackbarContext, SnackbarContext, TSnackbarMessageLevel } from './contexts/snackbar';

const icons = { Info, Warning, Error };

const styles = (theme: Theme) =>
  createStyles({
    closeSnackbar: {
      width: theme.spacing(4),
      // height: theme.spacing.unit * 4,
    },
    snackbar: {
      marginTop: '4em',
    },
    snackContainer: {
      display: 'inline-block',
    },
    snackMessage: {
      display: 'inline-block',
      marginLeft: '10px',
      verticalAlign: 'middle',
    },
    errorIcon: {
      color: '#ff6251',
      verticalAlign: 'middle',
    },
    infoIcon: {
      color: '#75beff',
      verticalAlign: 'middle',
    },
  });

interface Message {
  content: string;
  key: number;
  level: TSnackbarMessageLevel;
}

interface State {
  open: boolean;
  message?: Message;
  context: ISnackbarContext;
  onClose: () => void;
}

// NOTE: Should be aligned with the Snackbar.spawn method.
export type SnackbarSpawn = (message: string, level: TSnackbarMessageLevel, onClose?: () => void) => undefined | void;

class Snackbar extends React.Component<WithStyles<typeof styles>, State> {
  public queue: Message[] = [];

  public readonly state: Readonly<State> = {
    open: false,
    context: {
      spawn: this.spawn.bind(this),
    },
    onClose: () => undefined,
  };

  public spawn(message: string, level: TSnackbarMessageLevel = 'info', onClose = (): void => undefined): void {
    this.queue.push({
      key: Date.now(),
      content: message,
      level,
    } as Message);

    if (this.state.open) {
      this.setState({ open: false });
    } else {
      this.processQueue(onClose);
    }
  }

  public processQueue = (onClose: () => void) => {
    if (this.queue.length > 0) {
      this.setState({
        message: this.queue.shift(),
        open: true,
        onClose,
      });
    }
  };

  public handleClose = (_event: React.SyntheticEvent<any>, reason: string = '') => {
    this.state.onClose();

    if (reason === 'clickaway') {
      return;
    }
    this.setState({ open: false });
  };

  public handleExited = () => {
    this.processQueue(this.state.onClose);
  };

  public render() {
    const { message, open, context } = this.state;
    const { classes, children } = this.props;

    const level = (message && message.level) || 'info';

    const Icon = icons[capitalize(level) as keyof typeof icons];
    const messageContent = (
      <>
        <Icon className={classes[`${level}Icon` as keyof typeof classes]} />
        <span className={classes.snackMessage}>{(message && message.content) || ''}</span>
      </>
    );

    const action = (
      <IconButton
        key="close"
        aria-label="Close"
        color="inherit"
        className={classes.closeSnackbar}
        onClick={this.handleClose}
      >
        <Close />
      </IconButton>
    );

    return (
      <SnackbarContext.Provider value={context}>
        {children}
        <MuiSnackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={open}
          autoHideDuration={6000}
          onClose={this.handleClose}
          onExited={this.handleExited}
          className={classes.snackbar}
          message={messageContent}
          action={action}
        />
      </SnackbarContext.Provider>
    );
  }
}

export default withStyles(styles)(Snackbar);
